import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useStore } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { inr } from "@/lib/format";
import { Search, ArrowRight } from "lucide-react";

export default function StorefrontHome() {
  const { slug } = useParams();
  const { tenant } = useStore();
  const [params, setParams] = useSearchParams();
  const [banners, setBanners] = useState([]);
  const [offers, setOffers] = useState([]);
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState(params.get("cat") || "");

  useEffect(() => {
    api.get(`/store/${slug}/banners?position=home`).then(({ data }) => setBanners(data));
    api.get(`/store/${slug}/offers`).then(({ data }) => setOffers(data));
    api.get(`/store/${slug}/categories`).then(({ data }) => setCats(data));
  }, [slug]);

  useEffect(() => {
    const qq = {};
    if (activeCat) qq.category_id = activeCat;
    if (q) qq.q = q;
    const url = `/store/${slug}/products?${new URLSearchParams(qq).toString()}`;
    api.get(url).then(({ data }) => setProducts(data));
  }, [slug, activeCat, q]);

  const b = tenant?.branding || {};
  const isDark = b.theme === "automotive";

  return (
    <div className="max-w-6xl mx-auto px-4 pb-12">
      {/* Hero / banner */}
      {banners[0] && (
        <div className="rounded-2xl overflow-hidden relative mt-4" data-testid="storefront-hero-banner">
          <img src={banners[0].image} alt={banners[0].title} className="w-full h-48 md:h-72 object-cover" />
          <div className={`absolute inset-0 ${isDark ? "bg-gradient-to-t from-black via-black/40" : "bg-gradient-to-t from-black/60 via-black/0"}`} />
          <div className="absolute bottom-0 left-0 p-6 md:p-10 text-white max-w-2xl">
            <h1 style={{ fontFamily: b.heading_font }} className="text-3xl md:text-5xl font-bold tracking-tight">{banners[0].title}</h1>
            <Button className="t-btn mt-4" style={{ background: b.primary_color }} data-testid="hero-cta-btn" asChild>
              <a href="#products">Browse rentals <ArrowRight className="ml-2 h-4 w-4" /></a>
            </Button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mt-6 flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-60" />
          <Input className="pl-9" placeholder="Search products..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="storefront-search-input" />
        </div>
      </div>

      {/* Categories scroller */}
      {cats.length > 0 && (
        <div className="mt-6 overflow-x-auto no-scrollbar">
          <div className="flex gap-2 pb-2">
            <button onClick={() => setActiveCat("")} data-testid="category-chip-all" className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap border transition ${!activeCat ? "" : "opacity-60"}`} style={{ borderColor: b.primary_color, background: !activeCat ? b.primary_color : "transparent", color: !activeCat ? "#fff" : b.primary_color }}>All</button>
            {cats.map((c) => (
              <button key={c.id} onClick={() => setActiveCat(c.id)} data-testid={`category-chip-${c.id}`} className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap border transition ${activeCat === c.id ? "" : "opacity-60"}`} style={{ borderColor: b.primary_color, background: activeCat === c.id ? b.primary_color : "transparent", color: activeCat === c.id ? "#fff" : b.primary_color }}>{c.name}</button>
            ))}
          </div>
        </div>
      )}

      {/* Offers */}
      {offers.length > 0 && (
        <section className="mt-8">
          <h2 className="text-xl font-bold mb-3" style={{ fontFamily: b.heading_font }}>Featured Offers</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {offers.slice(0, 4).map((o) => (
              <div key={o.id} className="t-card t-surface p-5 flex gap-4 items-center" data-testid={`offer-card-${o.id}`}>
                {o.image && <img src={o.image} alt="" className="w-20 h-20 object-cover rounded-md" />}
                <div className="flex-1">
                  <div className="text-2xl font-bold" style={{ color: b.primary_color, fontFamily: b.heading_font }}>{o.discount_type === "percent" ? `${o.discount_value}% OFF` : `${inr(o.discount_value)} OFF`}</div>
                  <div className="font-medium">{o.title}</div>
                  <div className="text-xs opacity-70 mt-1">{o.description}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Products */}
      <section className="mt-10" id="products">
        <h2 className="text-xl font-bold mb-4" style={{ fontFamily: b.heading_font }}>{activeCat ? cats.find((c) => c.id === activeCat)?.name : "All Rentals"}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {products.map((p) => {
            const price = p.pricing?.daily || p.pricing?.monthly || p.pricing?.hourly || p.pricing?.weekly || p.pricing?.yearly;
            const unit = p.pricing?.daily ? "/day" : p.pricing?.monthly ? "/month" : p.pricing?.hourly ? "/hr" : p.pricing?.weekly ? "/week" : "/year";
            return (
              <Link key={p.id} to={`/t/${slug}/product/${p.id}`} data-testid={`product-card-${p.id}`} className="t-card t-surface overflow-hidden group hover:-translate-y-0.5 transition-transform">
                <div className="aspect-square overflow-hidden" style={{ background: isDark ? "#1a1a1a" : "#f8fafc" }}>
                  {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />}
                </div>
                <div className="p-3">
                  <div className="font-semibold text-sm line-clamp-1">{p.name}</div>
                  <div className="text-xs opacity-60 mt-0.5 line-clamp-1">{p.description}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="font-bold text-lg" style={{ color: b.primary_color }}>{inr(price)}</span>
                    <span className="text-xs opacity-60">{unit}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        {products.length === 0 && <Card className="p-10 text-center opacity-60">No products match.</Card>}
      </section>
    </div>
  );
}
